export type Company = {
  id: string;
  name: string;
};

export interface ApiResponse<T = any> {
  status: boolean;
  statusCode: number;
  data: T;
  /**
   * Human-readable failure text. Typed `string[]`, but the wire sends a bare
   * `string` on some routes (notably a 413 from the upload endpoint) — always
   * read it through `normalizeApiErrors`, never `.length` or `Array.isArray`
   * at a call site.
   *
   * NOT a contract. The prose varies by environment: the backend's
   * `ErrorDisplayOptions:IncludeDiagnosticDetail` setting makes it detailed in
   * Development and generic in Production. Never key UI behaviour on its text —
   * key on `errorCode` below.
   */
  errors: string[];
  /**
   * Stable machine-readable failure code. Present only on errors that define
   * one, and omitted entirely from the JSON when null — so every response shape
   * that predates this field is unchanged, and a missing `errorCode` simply
   * means "generic failure, fall back to `errors`".
   *
   * This IS the contract, and it is what UI copy should switch on. See
   * `fs-validation-errors.util.ts` for the filing-session upload codes, and
   * `login-errors.util.ts` for the login rate-limit code.
   */
  errorCode?: string;
}

export enum MbrsStatusEnum {
  Failed = 0,
  Success = 1,
  InProgress = 2,
  Review = 3,
  /**
   * Backend-only state — Filing session created and waiting for file uploads.
   * Already in production on the backend; mirrored here for completeness so
   * client code that switches on the enum is exhaustive.
   */
  AwaitingFiles = 4,
  /**
   * AS-842 — Backend has finished its conversion work; awaiting the frontend's
   * silent validation verdict. Visually rendered as "In Progress" (same chip
   * as `InProgress`) so users see a single "still working" state. Frontend
   * derives the real terminal status (Success / SuccessWithWarnings / Review)
   * via `ConversionStatusResolverService` and POSTs it back via
   * `/api/v2/mbrs/update-conversion-status`.
   *
   * Writer-separation invariant: the BACKEND is the sole writer of this value
   * (set on completion of N8N callbacks). Never POST `PendingValidation` from
   * the frontend.
   */
  PendingValidation = 5,
  /**
   * AS-842 — Frontend-derived terminal: no errors, ≥1 sheet has a warning
   * (e.g. calculated total ≠ N8N-extracted total). Rendered as "Success*".
   *
   * Writer-separation invariant: the FRONTEND is the sole writer of this
   * value, via the silent-validation resolver after a `PendingValidation`
   * row resolves.
   */
  SuccessWithWarnings = 6,
}

/**
 * Mirrors the backend `ConversionPhaseEnum`. Integer values are stable
 * (Phase1 = 1, Phase2 = 2). Phase 1 = only Filing Information + Scope of
 * Filing are editable; Phase 2 = the remaining sheets become editable after
 * the user clicks "proceed" following the Filing Info review.
 *
 * Backend now exposes `phase` on the Conversion REST payload — entity
 * `Conversion.Phase` (defaults to Phase1) surfaced via
 * `ConversionListingResponseDto.Phase`. No `JsonStringEnumConverter` is
 * registered server-side, so it serialises as the numeric value (1 / 2),
 * matching the integers above. The `Receive1st/2ndConversionProgress` SignalR
 * payloads carry it too. The field is kept optional here so consumers tolerate
 * `undefined` for pre-rollout / older cached rows; always read it via
 * `isPhase1` / `isPhase2`.
 *
 * Developer Note: confirm at runtime that the conversion-listing query
 * actually projects `Phase` into the DTO (log one listing row's `phase`).
 */
export enum ConversionPhaseEnum {
  Phase1 = 1,
  Phase2 = 2,
}

/**
 * The audit-trail slice of a conversion — what the delete dialog shows beside
 * the company and status. Its only source is the `xbrl-data-listing` row (the
 * filing page already reads it for the file name and status). Kept as a `Pick`
 * of `ConversionData` so it can never drift from those names, but every field
 * is optional: when the read omits a value we show a dash rather than invent
 * one (see {@link DeleteConversionTarget}).
 *
 * Future rework: if the backend refactors `xbrl-data-listing`, re-check that it still returns
 * `conversionDate`, `updatedDate`, `createdBy` and `updatedBy` under these
 * names; this type and its two consumers (`applyConversionListingResponse`,
 * the role0/role1 `conversionData` getters) are the only places to change.
 */
export type ConversionAuditTrail = Partial<
  Pick<ConversionData, 'conversionDate' | 'updatedDate' | 'createdBy' | 'updatedBy'>
>;

/**
 * What the delete dialog needs to describe a conversion. The same as
 * {@link ConversionData} except that `conversionDate` may be missing, so the
 * filing page can hand over a conversion whose audit read failed and the dialog
 * shows "-" instead of a made-up "now".
 */
export type DeleteConversionTarget = Omit<ConversionData, 'conversionDate'> & {
  conversionDate?: Date;
};

export interface ConversionData {
  id: string;
  fileName: string;
  company: string;
  auditYear: string;
  status: MbrsStatusEnum;
  /**
   * Backend-supplied phase (`ConversionListingResponseDto.Phase`). Kept
   * optional so consumers tolerate `undefined` on pre-rollout / older cached
   * rows. Prefer `isPhase1` / `isPhase2` over reading this directly so the
   * `undefined` case stays centralised.
   */
  phase?: ConversionPhaseEnum;
  /**
   * Engine that actually processed each phase, as recorded by the backend on
   * the `Conversion` row and surfaced on `ConversionListingResponseDto`.
   *
   * Strings on the wire (`"v1"`, `"v1.5"`, `"v2"`), never the backend enum's
   * ordinal — that enum is declared `{ v1, v2, v1_5 }` with no
   * `JsonStringEnumConverter`, so `2` would mean v1.5 while reading as "v2".
   *
   * Optional for the same reason `phase` is: rows predating the per-request
   * engine override carry neither, and the picker simply falls back to the
   * server default when both are absent.
   */
  phase1ConversionEngine?: string;
  phase2ConversionEngine?: string;
  conversionDate: Date;
  updatedDate?: Date;
  /**
   * Email (never a user id) of the user who created the conversion. Written
   * once by the backend and never changes. The backend sends `null` for a
   * purged user; optional so that, and rows from a backend build that predates
   * the field, render a dash instead of breaking.
   */
  createdBy?: string;
  /**
   * Email of the last signed-in USER to modify the conversion. Machine updates
   * (N8N callbacks, mapping jobs, crash-resume) move `updatedDate` but not
   * this, so it means "last change by a user", not "who caused `updatedDate`".
   */
  updatedBy?: string;
  fileUrl: string;
  xbrlFileName?: string;
  mbrsData: MbrsData[];
  /**
   * Per-sheet validation status keyed by `MbrsSheetCatalog` slug. The backend
   * now sends an `MbrsStatusEnum` int per sheet (1 Success | 3 Review |
   * 6 SuccessWithWarnings) — NOT a boolean. Read it with explicit enum
   * comparisons; both `3` and `6` are truthy, so an un-migrated `if (map[slug])`
   * check would silently treat a Review/warning sheet as "passed".
   */
  sheetStatuses?: { [sheetSlug: string]: MbrsStatusEnum };
}

/**
 * SignalR payload for `Receive1stConversionProgress` and
 * `Receive2ndConversionProgress`. `phase` is carried alongside the REST field;
 * the listener boundary keeps `phase?` optional so a payload that predates the
 * field still compiles and is tolerated.
 */
export interface ConversionProgressPayload {
  conversionId: string;
  status: MbrsStatusEnum;
  phase?: ConversionPhaseEnum;
  companyName: string;
  fsFileName: string;
  /**
   * UTC ISO 8601 stamp of the row's `Conversion.UpdatedAt` at broadcast time,
   * used to refresh the listing's "Last Modified" cell without a refetch.
   *
   * Optional on purpose: the backend does not send it yet, and the listing
   * falls back to device local time when it is absent or unparseable. That
   * keeps this frontend shippable ahead of the backend change and makes the
   * eventual payload enrichment a no-op release here.
   *
   * Do NOT treat the painted value as exact. The backend stamps `UpdatedAt` a
   * second time when the status write-back POSTs (`UpdateConversionStatus`),
   * so the next `conversion-listing` fetch legitimately returns a later time
   * than whatever was painted at broadcast. This column is display-only.
   */
  updatedAt?: string;
}

/**
 * Phase predicates for `ConversionData`. Centralise the `undefined`-tolerant
 * read here instead of comparing `phase` inline — and never re-infer phase
 * from `typeOfSubmission` or `mbrsData` presence.
 *
 * Confirmed invariant (BE-confirmed, 2026-06-08): a Phase 1 conversion never
 * reaches `Success` / `SuccessWithWarnings`. It terminates at `Review` (or
 * stays `Failed` / `InProgress`); only Phase 2 can reach a Success terminal.
 * Two consequences hold across the UI with no explicit phase branch:
 *   - The "Successful Conversions" tile/count is inherently Phase-2-only — a
 *     Phase 1 row can never contribute to it.
 *   - XBRL download is gated on a Success terminal (`isDownloadable` in
 *     `utils/conversion-status.util.ts`), so a Phase 1 row is never
 *     downloadable.
 * `Review`, by contrast, genuinely spans both phases.
 */
export function isPhase1(conversion: Pick<ConversionData, 'phase'>): boolean {
  return conversion.phase === ConversionPhaseEnum.Phase1;
}

export function isPhase2(conversion: Pick<ConversionData, 'phase'>): boolean {
  return conversion.phase === ConversionPhaseEnum.Phase2;
}

export interface MbrsData {
  id: string;
  parentSheetId: string;
  value: string;
}

export interface ConversionDataListing {
  conversions: PaginatedResult<ConversionData>;
}

export interface PaginatedResult<T> {
  items: T[];
  totalCount: number;
  /** Echoed paging envelope from paginated endpoints (dev logs + reports).
   * Optional: error/empty fallbacks and older callers omit them. */
  page?: number;
  pageSize?: number;
  totalPages?: number;
}

export type ConversionListingDataResponse = ApiResponse<ConversionDataListing>;

export interface UserAuth {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isDev?: boolean;
  role?: string;
}

export type UserAuthResponse = ApiResponse<UserAuth>;

/**
 * `GET /api/user/profile`. Since 2026-10-20 there is no `companies` list: a
 * company dropdown takes its options from `GET /api/user/users` instead (already
 * scoped: an Admin gets their own company, a SuperAdmin all of them).
 */
export interface UserProfile {
  id: string;
  name: string;
  email: string;
  /**
   * The caller's own company NAME. `null` for a SuperAdmin, and also for a
   * User/Admin with no company assigned (a data fault), so "All Companies" is
   * keyed on the SuperAdmin role AND null, never on null alone.
   */
  company: string | null;
  pendingChanges: string[];
  roles: string[];
}

export type UserProfileResponse = ApiResponse<UserProfile>;

export interface UserManagementData {
  id: string;
  name: string;
  email: string;
  company: string;
  role: string;
  lastAdded: Date;
  lastUpdated: Date;
}

export interface UserManagementDataListing {
  users: PaginatedResult<UserManagementData>;
  companies: Company[];
  pendingUserChanges?: string[];
}

export type UserManagementDataResponse = ApiResponse<UserManagementDataListing>;

export interface EmailAvailabilityResponse {
  isEmailTaken: boolean;
}

export interface FormSchema {
  role: string;
  definition: string;
  sheetId: string;
  label: string;
  controls: FormControlSchema[];
}

export interface FormControlSchema {
  qname: string;
  label: string;
  type: string;
  ui: {
    control: string;
    placeholder?: string;
    // Per-control placeholder opt-out; default true. See utils/placeholder.util.ts.
    showPlaceholder?: boolean;
    enumSource?: string;
    readonly?: boolean;
    defaultValue?: string;
    static?: string[];
    autoManageByTypeOfSubmission?: boolean; // Auto-manage visibility and value based on Type of Submission
    autoManageByContext?: boolean; // Auto-manage visibility based on multi-field context (status, type, standards, submission)
    disabledWhen?: {
      field: string;
      values: string[];
      setValueTo?: string; // Optional: Set field to this value when disabled
      hideWhenDisabled?: boolean; // Optional: Hide field when disabled
    };
    optionsFilteredBy?: {
      field?: string; // Single field (backward compatibility)
      fields?: string[]; // Multiple fields for composite key filtering
      mappingSource?: string; // Reference to typeDict mapping (e.g., "conditional:TypeOfCompanyByStatus")
      mappings?: { [key: string]: string[] }; // Inline mappings (backward compatibility)
    };
  };
  required?: boolean;
  requiredIf?: { qname: string; equals: string };
  requiredIfAny?: Array<{ qname: string; equals: string }>;
  validators?: Array<{ name: string; patternSource?: string; other?: string }>;
  triggers?: Array<{
    on: string;
    ifEquals: string;
    then: Array<{ action: string; targets: string[] }>;
  }>;
}

export interface ParentSheet {
  id: string;
  sheetName: string;
  sheetLabel: string;
  sheetSlug: string;
}